import React, { useState, useEffect } from "react";
import api from "../services/api";
import { Link } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import SimpleTrendingCarousel from "../components/SimpleTrendingCarousel";
import SimpleNewReleases from "../components/SimpleNewReleases";
import {
  FaSearch,
  FaFilter,
  FaStar,
  FaCalendarAlt,
  FaSortAmountDown,
  FaPlay,
  FaHeart,
  FaBookmark,
  FaSpinner
} from "react-icons/fa";

function Discover() {
  const { isDark } = useTheme();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("");
  const [minRating, setMinRating] = useState("");
  const [fromYear, setFromYear] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setHasSearched(true);

    try {
      const params = {
        sort_by: sortBy || undefined,
        min_rating: minRating || undefined,
        from_year: fromYear || undefined,
      };

      if (query.trim()) params.query = query.trim();

      const res = await api.get("/movies/search", { params });
      setResults(res.data);
    } catch (err) {
      setError("Failed to fetch movies. Please try again.");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setQuery("");
    setSortBy("");
    setMinRating("");
    setFromYear("");
    setResults([]);
    setError("");
    setHasSearched(false);
  };

  const MovieCard = ({ movie }) => (
    <div className="group relative theme-bg-secondary rounded-lg sm:rounded-xl theme-shadow-lg overflow-hidden hover:theme-shadow-xl transition-all duration-300 transform hover:-translate-y-1 sm:hover:-translate-y-2">
      <Link to={`/movie/${movie.id}`} className="block">
        <div className="relative overflow-hidden">
          {movie.poster_path ? (
            <img
              src={`https://image.tmdb.org/t/p/w500${movie.poster_path}`}
              alt={movie.title}
              className="w-full h-64 sm:h-72 lg:h-80 object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-64 sm:h-72 lg:h-80 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 flex items-center justify-center">
              <div className="text-center text-gray-500 dark:text-gray-400">
                <FaPlay className="text-3xl sm:text-4xl mx-auto mb-2" />
                <span className="text-xs sm:text-sm">No Image Available</span>
              </div>
            </div>
          )}

          {/* Overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <FaPlay className="text-white text-3xl bg-white/20 rounded-full p-3 backdrop-blur-sm" />
            </div>
          </div>

          {/* Rating Badge */}
          {movie.vote_average > 0 && (
            <div className="absolute top-3 right-3 bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-bold flex items-center space-x-1">
              <FaStar className="text-xs" />
              <span>{movie.vote_average.toFixed(1)}</span>
            </div>
          )}
        </div>

        <div className="p-3 sm:p-4">
          <h3 className="font-bold theme-text-primary text-base sm:text-lg mb-2 line-clamp-2 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
            {movie.title}
          </h3>

          {movie.release_date && (
            <div className="flex items-center theme-text-tertiary text-xs sm:text-sm mb-2">
              <FaCalendarAlt className="mr-1 sm:mr-2 text-xs sm:text-sm" />
              <span>{new Date(movie.release_date).getFullYear()}</span>
            </div>
          )}

          {movie.overview && (
            <p className="theme-text-secondary text-xs sm:text-sm line-clamp-2 sm:line-clamp-3">
              {movie.overview}
            </p>
          )}
        </div>
      </Link>
    </div>
  );

  return (
    <div className="min-h-screen theme-bg-primary">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-700 text-white py-8 sm:py-12 lg:py-16">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 text-center">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold mb-3 sm:mb-4">
            Discover Amazing Movies
          </h1>
          <p className="text-sm sm:text-base md:text-xl lg:text-2xl text-white/90 mb-4 sm:mb-6 lg:mb-8 px-4">
            Find your next favorite film with our powerful search and filter tools
          </p>
        </div>
      </div>

      {/* Trending Movies Carousel */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        <SimpleTrendingCarousel />
      </div>

      {/* New Releases Section */}
      <SimpleNewReleases />

      {/* Search Section */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        <div className="theme-bg-secondary rounded-xl sm:rounded-2xl theme-shadow-xl p-4 sm:p-6 md:p-8 theme-border border">
          <form onSubmit={handleSearch} className="space-y-6">
            {/* Main Search Bar */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <FaSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search for movies, actors, or genres..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full pl-10 sm:pl-12 pr-4 py-3 sm:py-4 border-2 theme-border rounded-lg sm:rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-200 text-base sm:text-lg theme-bg-primary theme-text-primary placeholder-gray-500"
              />
            </div>

            {/* Filter Toggle */}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2 text-gray-600 hover:text-purple-600 transition-colors"
              >
                <FaFilter />
                <span>Advanced Filters</span>
              </button>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={clearFilters}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Clear All
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:from-purple-700 hover:to-blue-700 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      <span>Searching...</span>
                    </>
                  ) : (
                    <>
                      <FaSearch />
                      <span>Search Movies</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-xl">
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                    <FaSortAmountDown />
                    <span>Sort By</span>
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                  >
                    <option value="">Default</option>
                    <option value="popularity.desc">Most Popular</option>
                    <option value="release_date.desc">Newest First</option>
                    <option value="vote_average.desc">Highest Rated</option>
                    <option value="revenue.desc">Highest Grossing</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                    <FaStar />
                    <span>Minimum Rating</span>
                  </label>
                  <input
                    type="number"
                    placeholder="e.g., 7.0"
                    value={minRating}
                    onChange={(e) => setMinRating(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                    min="0"
                    max="10"
                    step="0.1"
                  />
                </div>

                <div className="space-y-2">
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                    <FaCalendarAlt />
                    <span>From Year</span>
                  </label>
                  <input
                    type="number"
                    placeholder="e.g., 2020"
                    value={fromYear}
                    onChange={(e) => setFromYear(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                    min="1900"
                    max={new Date().getFullYear()}
                  />
                </div>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Results Section */}
      {hasSearched && (
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-6 sm:py-8 lg:py-12">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          {results.length > 0 && (
            <div className="mb-6">
              <h2 className="text-2xl font-bold theme-text-primary mb-2">
                Search Results
              </h2>
              <p className="theme-text-secondary">
                Found {results.length} movie{results.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}

          {results.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {results.map(movie => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          ) : !loading && (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">🎬</div>
              <h3 className="text-2xl font-bold theme-text-primary mb-2">
                No Movies Found
              </h3>
              <p className="theme-text-secondary mb-8">
                Try adjusting your search terms or filters
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Discover;